// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
import type { Band, DSNSnapshot } from '../types';
import { extractPairs } from '../state';
import { getSpacecraftInfo } from '../data/spacecraft';
import { BAND_COLOR } from '../utils/bands';

export function renderMatrix(snapshot: DSNSnapshot | null): string {
  if (!snapshot) return `<div class="matrix-wrap"><p style="color: var(--text-tertiary);">Loading…</p></div>`;

  const pairs = extractPairs(snapshot).filter(
    (p) => p.spacecraft && p.spacecraft !== 'DSN' && p.spacecraft !== 'DSS',
  );

  const dishes: string[] = [];
  for (const complex of snapshot.complexes) {
    for (const dish of complex.dishes) {
      dishes.push(dish.name);
    }
  }
  const spacecraft = [...new Set(pairs.map((p) => p.spacecraft))].sort();

  if (spacecraft.length === 0) {
    return `
      <div class="view-header">
        <h2 class="view-title">Dish × Spacecraft matrix</h2>
        <p class="view-subtitle">No active tracks right now — matrix will fill in when antennas lock onto spacecraft.</p>
      </div>
      <div class="matrix-wrap"><p style="text-align:center; color: var(--text-tertiary); padding: var(--space-2xl);">Nothing to show.</p></div>
    `;
  }

  const lookup = new Map<string, { bands: string[]; active: boolean }>();
  for (const p of pairs) {
    const key = `${p.dish}|${p.spacecraft}`;
    lookup.set(key, { bands: p.bands, active: p.activeDown || p.activeUp });
  }

  const headerCols = spacecraft.map((s) => `<th class="col">${getSpacecraftInfo(s).name}</th>`).join('');

  const rows = dishes
    .map((d) => {
      const cells = spacecraft
        .map((s) => {
          const cell = lookup.get(`${d}|${s}`);
          if (!cell) {
            const emptyLabel = `${d} — ${getSpacecraftInfo(s).name}: no link`;
            return `<td><div class="matrix-cell" data-tip="${emptyLabel}" aria-label="${emptyLabel}"></div></td>`;
          }
          const band = (cell.bands[0] ?? '') as Band;
          const color = BAND_COLOR[band] ?? '#7f8c8d';
          const opacity = cell.active ? 1 : 0.4;
          const label = `${d} ↔ ${getSpacecraftInfo(s).name}${band ? ` · ${band}-band` : ''}${cell.active ? ' · active' : ' · scheduled'}`;
          return `<td><div class="matrix-cell filled" style="background: ${color}; opacity: ${opacity}" data-tip="${label}" aria-label="${label}"></div></td>`;
        })
        .join('');
      return `<tr><th>${d}</th>${cells}</tr>`;
    })
    .join('');

  const bandLegend = Object.entries(BAND_COLOR)
    .filter(([k]) => k !== '')
    .map(([k, c]) => `<span style="display:inline-flex;align-items:center;gap:4px;color:var(--text-secondary);font-size: var(--font-size-xs);"><span style="width:10px;height:10px;border-radius:2px;background:${c};"></span>${k}-band</span>`)
    .join(' · ');

  return `
    <div class="view-header">
      <h2 class="view-title">Dish × Spacecraft matrix</h2>
      <p class="view-subtitle">
        Every antenna as a row, every currently-tracked spacecraft as a column. Cell colour is the primary
        <span class="glossary-link" data-term="xband">band</span>; faint cells mean the track is scheduled but the link is momentarily inactive.
        Rows with multiple filled cells are demonstrating <span class="glossary-link" data-term="mspa">MSPA</span>.
      </p>
    </div>
    <div class="matrix-wrap">
      <table class="matrix-table">
        <thead><tr><th></th>${headerCols}</tr></thead>
        <tbody>${rows}</tbody>
      </table>
      <div style="display:flex; gap: var(--space-md); flex-wrap: wrap; margin-top: var(--space-lg);">
        ${bandLegend}
      </div>
    </div>
  `;
}
